import { useEffect, useRef, useMemo } from 'react';
import {
    CheckCircle2, AlertCircle, Clock, Sparkles, Loader2,
    Terminal, ChevronRight, Zap
} from 'lucide-react';

/* ──────────────────────────────────────────────────────────────
 * AgentConsole — real-time streaming log of Full-Stack Build
 * Props:
 *   events[]       – { type, message, timestamp, data? }
 *   planner        – planner object with tasks[]
 *   research       – research brief object
 *   building       – boolean, true while build is in progress
 *   buildSummary   – summary object set on completion
 * ────────────────────────────────────────────────────────────── */

const iconConfig = {
    success: { icon: CheckCircle2, cls: 'text-green-400' },
    complete: { icon: CheckCircle2, cls: 'text-green-400' },
    error: { icon: AlertCircle, cls: 'text-red-400' },
    warning: { icon: AlertCircle, cls: 'text-yellow-400' },
    info: { icon: Clock, cls: 'text-blue-400' },
    phase: { icon: Sparkles, cls: 'text-orange-400' },
    plan_update: { icon: Sparkles, cls: 'text-purple-400' },
    plan: { icon: Sparkles, cls: 'text-orange-400' },
    research: { icon: Sparkles, cls: 'text-emerald-400' },
};

const taskStatusIcon = {
    pending: { icon: Clock, cls: 'text-slate-500' },
    in_progress: { icon: Loader2, cls: 'text-cyan-400 animate-spin' },
    completed: { icon: CheckCircle2, cls: 'text-green-400' },
    complete: { icon: CheckCircle2, cls: 'text-green-400' },
    blocked: { icon: AlertCircle, cls: 'text-yellow-400' },
    failed: { icon: AlertCircle, cls: 'text-red-400' },
};

const SA_TZ = 'Africa/Johannesburg';

const fmtTime = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: SA_TZ });
};

const AgentConsole = ({ events = [], planner, research, building, buildSummary }) => {
    const scrollRef = useRef(null);

    /* auto-scroll on new events — contained inside this panel only,
       never propagates to ancestor scroll containers or window. */
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Direct scrollTop write — does NOT walk ancestors like scrollIntoView would.
        el.scrollTop = el.scrollHeight;
    }, [events.length, planner, building]);

    /* derive active + next task */
    const { activeTask, nextTask, completedCount, totalTasks } = useMemo(() => {
        if (!planner?.tasks?.length) return {};
        const tasks = planner.tasks;
        return {
            activeTask: tasks.find(t => t.status === 'in_progress'),
            nextTask: tasks.find(t => t.status === 'pending'),
            completedCount: tasks.filter(t => t.status === 'completed' || t.status === 'complete').length,
            totalTasks: tasks.length,
        };
    }, [planner]);

    const isEmpty = events.length === 0 && !planner && !building;

    return (
        <div className="flex flex-col h-full bg-slate-950/80 rounded-lg border border-slate-700/50 overflow-hidden">
            {/* header bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-700/50 bg-slate-900/60 shrink-0">
                <Terminal className="h-4 w-4 text-orange-400" />
                <span className="text-sm font-semibold text-slate-200 tracking-wide">Agent Console</span>
                {building && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-cyan-300">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Running
                    </span>
                )}
                {!building && buildSummary && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-green-400">
                        <CheckCircle2 className="h-3 w-3" />
                        Complete
                    </span>
                )}
            </div>

            {/* scrollable log body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-1 font-mono text-xs scroll-smooth">
                {isEmpty && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500">
                        <Terminal className="h-8 w-8 mb-2 opacity-40" />
                        <span>Waiting for build&hellip;</span>
                    </div>
                )}

                {/* Research brief (compact) */}
                {research && Array.isArray(research.summary) && research.summary.length > 0 && (
                    <div className="mb-2 rounded border border-emerald-700/40 bg-emerald-950/30 px-3 py-2">
                        <div className="flex items-center gap-1.5 text-emerald-400 font-semibold text-[11px] uppercase tracking-wider mb-1">
                            <Sparkles className="h-3 w-3" />
                            Research Brief
                        </div>
                        {research.summary.slice(0, 5).map((line, i) => (
                            <div key={i} className="text-slate-300 leading-relaxed">
                                <span className="text-emerald-500 mr-1">•</span>{line}
                            </div>
                        ))}
                    </div>
                )}

                {/* Task progress bar (compact horizontal) */}
                {planner?.tasks?.length > 0 && (
                    <div className="mb-2 rounded border border-slate-700/40 bg-slate-900/40 px-3 py-2">
                        <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
                            <span className="uppercase tracking-wider font-semibold">Tasks</span>
                            <span>{completedCount}/{totalTasks} done</span>
                        </div>
                        {/* mini progress bar */}
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-2">
                            <div
                                className="h-full bg-gradient-to-r from-cyan-500 to-green-500 rounded-full transition-all duration-500"
                                style={{ width: totalTasks ? `${(completedCount / totalTasks) * 100}%` : '0%' }}
                            />
                        </div>
                        {activeTask && (
                            <div className="flex items-center gap-1.5 text-cyan-300">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                <span className="truncate">{activeTask.title}</span>
                            </div>
                        )}
                        {nextTask && !activeTask && (
                            <div className="flex items-center gap-1.5 text-slate-400">
                                <ChevronRight className="h-3 w-3" />
                                <span className="truncate">Next: {nextTask.title}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Planner tasks (collapsed list) */}
                {planner?.tasks?.length > 0 && (
                    <div className="mb-2 space-y-0.5">
                        {planner.tasks.map((task) => {
                            const cfg = taskStatusIcon[task.status] || taskStatusIcon.pending;
                            const Icon = cfg.icon;
                            return (
                                <div
                                    key={task.id}
                                    className={`flex items-center gap-2 px-2 py-1 rounded ${task.status === 'in_progress'
                                        ? 'bg-cyan-500/10 border border-cyan-500/20'
                                        : 'hover:bg-slate-800/40'
                                        }`}
                                >
                                    <Icon className={`h-3 w-3 shrink-0 ${cfg.cls}`} />
                                    <span className={`truncate ${task.status === 'completed' || task.status === 'complete'
                                        ? 'text-slate-500 line-through'
                                        : task.status === 'in_progress'
                                            ? 'text-cyan-200'
                                            : 'text-slate-400'
                                        }`}>
                                        {task.title}
                                    </span>
                                    {task.status === 'in_progress' && (
                                        <span className="ml-auto text-[10px] text-cyan-400 shrink-0">active</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Event stream */}
                {events.map((evt, idx) => {
                    const cfg = iconConfig[evt.type] || iconConfig.info;
                    const Icon = cfg.icon;
                    const ts = fmtTime(evt.timestamp);
                    return (
                        <div key={idx} className="flex items-start gap-2 leading-snug">
                            <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${cfg.cls}`} />
                            <span className="text-slate-300 flex-1 break-words">{evt.message}</span>
                            {ts && <span className="text-slate-600 shrink-0 text-[10px] mt-0.5">{ts}</span>}
                        </div>
                    );
                })}

                {/* Building pulse */}
                {building && events.length > 0 && (
                    <div className="flex items-center gap-2 text-slate-500 pt-1">
                        <Zap className="h-3 w-3 animate-pulse text-orange-500/60" />
                        <span className="animate-pulse">Agent working&hellip;</span>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AgentConsole;
